/**
 * TOUR-03 lot 5 — calibration des curseurs de temps depuis l'historique (R-TOUR-03-3 amendée, B-2 / B-3). Aucune dépendance React.
 * Règle : une calibration est affichée avec sa source (n journées, période) ; aucune valeur calibrée ne s'applique sans clic.
 * Les noms de champs viennent de `GET /simulations/calibration` (api : SimulationCalibration).
 */

import {dateJJMM} from './atelierRules';

export const JOURNEES_CALIBRATION = 10;

export const CLES_CALIBREES = ['miseEnPlaceMin', 'tBacMin', 'kSeul'];

const fr = (n, decimales = 1) => Number(n).toLocaleString('fr-FR', {minimumFractionDigits: 0, maximumFractionDigits: decimales});

/** Nombre de journées retenues par la calibration (0 si la réponse est absente ou vide). */
export function nbJourneesCalibration(cal) {
    return Number(cal?.source?.journees) || 0;
}

/**
 * Journées écartées : `source.exclues = {total, bacsInsuffisants, fenetreCourte, autreEquipage}` ; tolère un entier ou une liste.
 */
export function nbExclues(cal) {
    const e = cal?.source?.exclues;
    if (Array.isArray(e)) return e.length;
    if (e && typeof e === 'object') {
        if (Number.isFinite(Number(e.total))) return Number(e.total);
        if (Number.isFinite(Number(e.nombre))) return Number(e.nombre);
        return Object.values(e).reduce((t, v) => t + (Array.isArray(v) ? v.length : Number(v) || 0), 0);
    }
    return Number(e) || 0;
}

const LIBELLE_MOTIF = {
    bacsInsuffisants: 'trop peu de bacs',
    fenetreCourte: 'fenêtre trop courte',
    autreEquipage: 'autre équipage que le dominant',
    equipageInconnu: 'équipage inconnu',
};

/** Motifs d'écartement non nuls : « 2 avec trop peu de bacs », « 1 avec fenêtre trop courte »… */
export function motifsExclusion(cal) {
    const e = cal?.source?.exclues;
    if (!e || typeof e !== 'object' || Array.isArray(e)) return [];
    return Object.keys(LIBELLE_MOTIF).filter((k) => Number(e[k]) > 0).map((k) => `${e[k]} ${k === 'autreEquipage' || k === 'equipageInconnu' ? '' : 'avec '}${LIBELLE_MOTIF[k]}`.replace('  ', ' '));
}

/** Écartées d'une calibration qui a retenu des journées : « 1 journée écartée (1 avec trop peu de bacs) » ; null sinon. */
export function texteEcartees(cal) {
    const n = nbExclues(cal);
    if (n === 0 || nbJourneesCalibration(cal) === 0) return null;
    const motifs = motifsExclusion(cal);
    return `${n} ${n > 1 ? 'journées écartées' : 'journée écartée'}${motifs.length ? ` (${motifs.join(', ')})` : ''}`;
}

/** Une proposition n'est applicable que si des journées ont été retenues ET que l'API en propose une. */
export function peutAppliquer(cal) {
    return nbJourneesCalibration(cal) > 0 && cal?.proposition != null;
}

export const libelleEquipageDominant = (e) => (e === 'binome' ? 'binôme' : e === 'seul' ? 'seul' : null);

/** « régression, r² = 0,83 » ou « médiane ». */
export function texteMethode(cal) {
    if (cal?.methode === 'regression') {
        const r2 = cal?.regression?.r2;
        return r2 != null ? `régression, r² = ${fr(r2, 2)}` : 'régression';
    }
    return 'médiane';
}

/**
 * B-3 — source complète : « 9 journées du 12/08 au 08/10, équipage dominant seul, médiane 4,1 min/bac (3,5–4,6), méthode médiane ».
 * Sans journée : « Aucun historique de tournée sur ce patrimoine ».
 */
export function texteSource(cal) {
    const n = nbJourneesCalibration(cal);
    if (n === 0) {
        const motifs = motifsExclusion(cal);
        return nbExclues(cal) > 0
            ? `Aucune journée retenue : ${nbExclues(cal)} ${nbExclues(cal) > 1 ? 'écartées' : 'écartée'}${motifs.length ? ` (${motifs.join(', ')})` : ''}`
            : 'Aucun historique de tournée sur ce patrimoine';
    }
    const m = cal.minParBac;
    const equipage = libelleEquipageDominant(cal.equipageDominant);
    return [
        `${n} ${n > 1 ? 'journées' : 'journée'} du ${dateJJMM(cal.source.du)} au ${dateJJMM(cal.source.au)}`,
        equipage ? `équipage dominant ${equipage}` : null,
        m ? `médiane ${fr(m.mediane)} min/bac (${fr(m.q1)}–${fr(m.q3)})` : null,
        `méthode ${texteMethode(cal)}`,
    ].filter(Boolean).join(', ');
}

/** Pastilles de la proposition : « mise en place 2 min », « t_bac 2,1 min/bac », « k 1 ». */
export function pastillesProposition(proposition) {
    if (!proposition) return [];
    return [
        `mise en place ${fr(proposition.miseEnPlaceMin)} min`,
        `t_bac ${fr(proposition.tBacMin)} min/bac`,
        `k ${fr(proposition.kSeul)}`,
    ];
}

/** `GET /simulations/calibration` : paramètres de la requête. */
export function paramsCalibration(patrimoine, journees = JOURNEES_CALIBRATION) {
    return {patrimoine, journees};
}

/**
 * B-2 — « Appliquer » : remplit exactement les trois champs avec la proposition et marque la source. Les valeurs ne changent
 * qu'ici : afficher la carte ne modifie jamais le formulaire.
 */
export function appliquerCalibration(valeurs, cal) {
    if (!peutAppliquer(cal)) return valeurs;
    const p = cal.proposition;
    return {
        ...valeurs,
        miseEnPlaceMin: p.miseEnPlaceMin,
        tBacMin: p.tBacMin,
        kSeul: p.kSeul,
        calibre: {journees: nbJourneesCalibration(cal), miseEnPlaceMin: p.miseEnPlaceMin, tBacMin: p.tBacMin, kSeul: p.kSeul},
    };
}

/** Nombre de journées de la calibration appliquée tant que les trois valeurs sont celles de la proposition, sinon null. */
export function journeesCalibrees(valeurs) {
    const c = valeurs?.calibre;
    if (!c) return null;
    return CLES_CALIBREES.every((k) => Number(valeurs[k]) === Number(c[k])) ? c.journees : null;
}

/** Marque de section : « calibré depuis 9 journées ». */
export function marqueSection(valeurs) {
    const n = journeesCalibrees(valeurs);
    return n == null ? null : `calibré depuis ${n} ${n > 1 ? 'journées' : 'journée'}`;
}

/** Mention du récapitulatif / bandeau : « curseurs calibrés (9 journées) ». */
export function mentionRecap(valeurs) {
    const n = journeesCalibrees(valeurs);
    return n == null ? null : `curseurs calibrés (${n} ${n > 1 ? 'journées' : 'journée'})`;
}
